import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting } from "@nosto/preact"
import { useMemo } from "preact/hooks"

import ProductMedia from "./product/ProductMedia"
import QuickBuy from "./product/QuickBuy"
import ColorSwatches from "./product/ColorSwatches"
import { useProductVariantsData } from "../hooks/useProductVariantsData"
import { useQuickBuyState } from "../hooks/useQuickBuyState"
import { productImagePlaceholder } from "../helpers"
import { handleize } from "../helpers/productVariants"
import { getBadge, getBadgeImage } from "../helpers/productBadges"
import { useProductPricing } from "../hooks/useProductPricing"

export default function Product({ product }) {
    const { format } = useCurrencyFormatting()

    const {
        optionIndexes,
        variants,
        hasQuickBuy,
        productOptions,
        swatches,
        shopifyImages
    } = useProductVariantsData(product)

    const {
        quickBuyRef,
        showQuickBuyOptions,
        setShowQuickBuyOptions,
        selectedSize,
        selectedColorName,
        selectedColor,
        hoverColorName,
        isAddingToCart,
        setColorOption,
        mouseOverColorOption,
        mouseOutColorOption,
        handleQuickBuy,
        isSizeAvailable,
        colorAvailableVariants
    } = useQuickBuyState({
        variants,
        optionIndexes,
        swatches,
        product
    })

    const isColorAvailable = (color) => colorAvailableVariants(color).length > 0

    const badge = useMemo(() => getBadge(product), [product])
    const badgeImage = useMemo(() => getBadgeImage(product), [product])

    const activeVariant = useMemo(() => {
        if (!Array.isArray(variants) || variants.length === 0) return null

        const { colorOptionIndex, sizeOptionIndex } = optionIndexes || {}

        const getOptionValue = (variant, optionIndex) => {
            if (typeof optionIndex !== "number" || optionIndex < 0) return undefined
            return variant?.[`option${optionIndex + 1}`]
        }

        const matchesSelectedColor = (variant) => {
            if (typeof colorOptionIndex !== "number" || colorOptionIndex < 0) return true

            const optionValue = getOptionValue(variant, colorOptionIndex)
            if (!optionValue) return false

            if (selectedColorName && optionValue === selectedColorName) return true
            if (selectedColor && handleize(optionValue) === selectedColor) return true

            return !selectedColorName && !selectedColor
        }

        const variantsByColor = variants.filter(matchesSelectedColor)
        const colorScopedVariants = variantsByColor.length > 0 ? variantsByColor : variants

        if (typeof sizeOptionIndex === "number" && sizeOptionIndex > -1) {
            if (selectedSize) {
                const exactMatch = colorScopedVariants.find((variant) => {
                    return getOptionValue(variant, sizeOptionIndex) === selectedSize
                })

                if (exactMatch) return exactMatch
            }

            const availableWithColor = colorScopedVariants.find((variant) => variant?.available !== false)
            if (availableWithColor) return availableWithColor
        }

        const firstAvailable = colorScopedVariants.find((variant) => variant?.available !== false)
        if (firstAvailable) return firstAvailable

        return colorScopedVariants[0] || variants[0] || null
    }, [
        variants,
        optionIndexes,
        selectedColorName,
        selectedColor,
        selectedSize
    ])

    const scopedVariants = useMemo(() => {
        if (!Array.isArray(variants) || variants.length === 0) return []

        const { colorOptionIndex } = optionIndexes || {}

        if (typeof colorOptionIndex !== "number" || colorOptionIndex < 0) {
            return variants
        }

        const focusColorName = hoverColorName || selectedColorName || ""
        const focusColorSlug = hoverColorName
            ? handleize(hoverColorName)
            : (selectedColor || (selectedColorName ? handleize(selectedColorName) : ""))

        const matchesFocusedColor = (variant) => {
            const optionValue = variant?.[`option${colorOptionIndex + 1}`]

            if (!focusColorName && !focusColorSlug) return true
            if (!optionValue) return false

            if (focusColorName && optionValue === focusColorName) return true
            if (focusColorSlug && handleize(optionValue) === focusColorSlug) return true

            return false
        }

        const filtered = variants.filter(matchesFocusedColor)

        return filtered.length > 0 ? filtered : variants
    }, [
        variants,
        optionIndexes,
        hoverColorName,
        selectedColorName,
        selectedColor
    ])

    const {
        renderPrice
    } = useProductPricing({
        product,
        activeVariant,
        scopedVariants,
        format
    })

    const sortedSwatches = useMemo(() => {
        if (!Array.isArray(swatches) || swatches.length === 0) return swatches

        const available = []
        const unavailable = []

        swatches.forEach((color) => {
            if (isColorAvailable(color)) {
                available.push(color)
            } else {
                unavailable.push(color)
            }
        })

        return [...available, ...unavailable]
    }, [swatches, variants])

    const promotionText = typeof activeVariant?.promoText === "string"
        ? activeVariant.promoText
        : ""

    return (
        <div class="ns-product-wrapper ns-relative">
            <div class="ns-product ns-border-box ns-col-12 ns-col-xs-6 ns-col-l-4 ns-p-0 ns-my-0 ns-mx-0">
                <SerpElement
                    as="a"
                    hit={product}
                    ariaLabel={`Product ${product.name}`}
                    class="ns-clickable ns-text-undecorated ns-color-inherit ns-d-block"
                >
                    <ProductMedia
                        product={product}
                        fallbackImage={productImagePlaceholder}
                        onMouseLeave={() => setShowQuickBuyOptions(false)}
                        selectedColorName={selectedColorName}
                        swatches={swatches}
                        shopifyImages={shopifyImages}
                        badge={badge}
                        badgeImage={badgeImage}
                    >
                        <QuickBuy
                            productName={product.name}
                            hasQuickBuy={hasQuickBuy}
                            quickBuyRef={quickBuyRef}
                            showQuickBuyOptions={showQuickBuyOptions}
                            onOpen={() => setShowQuickBuyOptions(true)}
                            productOptions={productOptions}
                            isSizeAvailable={isSizeAvailable}
                            isAddingToCart={isAddingToCart}
                            selectedSize={selectedSize}
                            onQuickBuy={handleQuickBuy}
                        />
                    </ProductMedia>
                </SerpElement>

                <div class="plp-item__details ns-relative" data-nosto-element="product">
                    <p class="plp-item__details-title">
                        <SerpElement
                            as="a"
                            hit={product}
                            ariaLabel={`Product ${product.name}`}
                            class="ns-clickable ns-text-undecorated ns-color-inherit ns-d-block"
                        >
                            <span class="ns-clipped ns-text-four-lines ns-text-md-three-lines">
                                {product.name}
                            </span>
                        </SerpElement>
                    </p>

                    <p class="plp-item__details-price">
                        {renderPrice()}
                    </p>

                    {promotionText && (
                        <p
                            class="plp-item__promotion-text ns-product-promo"
                            dangerouslySetInnerHTML={{ __html: promotionText }}
                        />
                    )}
                </div>

                <ColorSwatches
                    swatches={sortedSwatches}
                    selectedColorName={selectedColorName}
                    selectedColor={selectedColor}
                    hoverColorName={hoverColorName}
                    onSelectColor={setColorOption}
                    onMouseOverColor={mouseOverColorOption}
                    onMouseOutColor={mouseOutColorOption}
                    isColorAvailable={isColorAvailable}
                    groupName={`group-${product.productId}`}
                />
            </div>
        </div>
    )
}
